import React, { useState, useEffect } from "react";
import axios from "axios";
import Menu from "../Menu";
import { useNavigate, useParams } from "react-router-dom";

const SubGroupDataAdd = () => {
  const { subgroupId, groupId } = useParams();
  const [formData, setFormData] = useState({
    posname: "",
    merchantid: "",
    storeid: "",
    remark: "",
    active: true,
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("");
  const [merchantList, setMerchantList] = useState([]);
  const [storeList, setStoreList] = useState([]);
  const [posList, setPosList] = useState([]);

  const navigate = useNavigate();
  const token = getCookie("token");
  const userid = getCookie("userid");

  // Handle input change
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  // Fetch merchants when dropdown is focused
  const fetchMerchants = async () => {
    try {
      const response = await axios.get(
        "https://api.dqr-display-tms.eventum.co.in/api/POSOperation/GetDistinct/merchantid",
        {
          headers: {
            "X-Authorization": token,
            UserId: userid,
            accept: "text/plain",
          },
        }
      );

      if (response.data && Array.isArray(response.data.data)) {
        const merchants = response.data.data.map((item) => item.merchantid);
        setMerchantList(merchants);
      }
    } catch (error) {
      console.error("Error fetching merchant list:", error);
      if (error.message === "relogin token expire") {
        navigate("/login");
      }
    }
  };

  // Fetch Stores based on the selected merchant
  const fetchStoreByMerchant = async (merchantId) => {
    try {
      const response = await axios.get(
        `https://api.dqr-display-tms.eventum.co.in/api/POSOperation/GetDistinct/specificstoreidbymerchantid_active/${merchantId}`,
        {
          headers: {
            "X-Authorization": token,
            UserId: userid,
            accept: "text/plain",
          },
        }
      );

      if (response.data && Array.isArray(response.data.data)) {
        const stores = response.data.data.map((item) => ({
          id: item.id,
          value: item.value,
        }));
        setStoreList(stores);
      } else {
        setStoreList([]);
      }
    } catch (error) {
      console.error("Error fetching store list:", error);
    }
  };

  // Fetch POS Names based on the selected store
  const fetchPosByStore = async (storeId) => {
    try {
      const response = await axios.get(
        `https://api.dqr-display-tms.eventum.co.in/api/POSOperation/GetDistinct/specificposbystoreid_active/${storeId}`,
        {
          headers: {
            "X-Authorization": token,
            UserId: userid,
            accept: "text/plain",
          },
        }
      );

      if (response.data && Array.isArray(response.data.data)) {
        const posNames = response.data.data.map((item) => ({
          id: item.id,
          value: item.value,
        }));
        setPosList(posNames);
      } else {
        setPosList([]);
      }
    } catch (error) {
      console.error("Error fetching POS list:", error);
    }
  };

  // Handle merchant selection
  const handleMerchantChange = (e) => {
    const selectedMerchant = e.target.value;
    setFormData({
      ...formData,
      merchantid: selectedMerchant,
      storeid: "",
      posname: "",
    });
    if (selectedMerchant) {
      fetchStoreByMerchant(selectedMerchant);
    } else {
      setStoreList([]);
      setPosList([]);
    }
  };

  // Handle store selection
  const handleStoreChange = (e) => {
    const selectedStore = e.target.value;
    setFormData({
      ...formData,
      storeid: selectedStore,
      posname: "",
    });
    if (selectedStore) {
      fetchPosByStore(selectedStore);
    } else {
      setPosList([]);
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.merchantid.trim()) {
      setMessage("Merchant ID is required");
      setMessageType("error");
      return;
    }
    if (!formData.storeid.trim()) {
      setMessage("Store ID is required");
      setMessageType("error");
      return;
    }
    if (!formData.posname.trim()) {
      setMessage("POS Name is required");
      setMessageType("error");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        subgroupid: parseInt(subgroupId),
        posname: formData.posname,
        merchantid: formData.merchantid,
        storeid: formData.storeid,
        remark: formData.remark || "",
        active: formData.active,
      };

      const response = await axios.post(
        "https://api.dqr-display-tms.eventum.co.in/api/SubGroupDataOperation/Insert",
        payload,
        {
          headers: {
            "X-Authorization": token,
            UserId: userid,
            "Content-Type": "application/json-patch+json",
            accept: "text/plain",
          },
        }
      );

      if (
        response.data?.isresponse === false &&
        response.data?.responsestatus === "FAIL"
      ) {
        // Error response from API
        setMessage(
          response.data?.errormsg ||
            response.data?.message ||
            "Failed to add record"
        );
        setMessageType("error");
      } else {
        // Success response
        setMessage(
          response.data?.message || "Record added successfully!"
        );
        setMessageType("success");

        // Reset form
        setFormData({
          posname: "",
          merchantid: "",
          storeid: "",
          remark: "",
          active: true,
        });

        // Redirect after 2 seconds
        setTimeout(() => {
          navigate(`/subgroupdatalist/${subgroupId}/${groupId}`);
        }, 2000);
      }
    } catch (error) {
      console.error("Error adding record:", error);
      setMessage(
        error.response?.data?.errormsg ||
          error.response?.data?.message ||
          error.message ||
          "Error adding record"
      );
      setMessageType("error");
    } finally {
      setLoading(false);
    }
  };

  // Clear message after 5 seconds
  React.useEffect(() => {
    if (message) {
      const timer = setTimeout(() => {
        setMessage("");
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  return (
    <>
      <Menu />
      <div className="wrapper">
        <div className="content-wrapper">
          <section className="content-header">
            <div className="container-fluid">
              <div className="d-flex justify-content-between align-items-center w-100">
                <div>
                  <h1 className="mb-0">Add SubGroup Data</h1>
                </div>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() =>
                    navigate(`/subgroupdatalist/${subgroupId}/${groupId}`)
                  }
                >
                  <i className="fas fa-arrow-left mr-1"></i>Back to List
                </button>
              </div>
            </div>
          </section>

          <section className="content">
            <div className="container-fluid">
              <div className="row">
                <div className="col-lg-6">
                  <div className="card">
                    <div className="card-header">
                      <h3 className="card-title">Add New Record</h3>
                    </div>
                    <form onSubmit={handleSubmit}>
                      <div className="card-body" style={{ backgroundColor: 'var(--surface)', color: 'var(--text)' }}>
                        <div className="form-group">
                          <label htmlFor="merchantid" style={{ color: 'var(--text)' }}>
                            Merchant ID <span className="text-danger">*</span>
                          </label>
                          <select
                            className="form-control"
                            id="merchantid"
                            name="merchantid"
                            value={formData.merchantid}
                            onChange={handleMerchantChange}
                            onFocus={fetchMerchants}
                            disabled={loading}
                            required
                            style={{
                              backgroundColor: 'var(--surface2)',
                              color: 'var(--text)',
                              border: '1px solid var(--border)'
                            }}
                          >
                            <option value="">Select Merchant ID</option>
                            {merchantList.map((merchant, index) => (
                              <option key={index} value={merchant}>
                                {merchant}
                              </option>
                            ))}
                          </select>
                          <small className="form-text text-muted" style={{ color: 'var(--text-muted)' }}>
                            Select the unique Merchant ID
                          </small>
                        </div>

                        <div className="form-group">
                          <label htmlFor="storeid" style={{ color: 'var(--text)' }}>
                            Store ID <span className="text-danger">*</span>
                          </label>
                          <select
                            className="form-control"
                            id="storeid"
                            name="storeid"
                            value={formData.storeid}
                            onChange={handleStoreChange}
                            disabled={loading || !formData.merchantid}
                            required
                            style={{
                              backgroundColor: 'var(--surface2)',
                              color: 'var(--text)',
                              border: '1px solid var(--border)'
                            }}
                          >
                            <option value="">Select Store ID</option>
                            {storeList.map((store, index) => (
                              <option key={index} value={store.value}>
                                {store.value}
                              </option>
                            ))}
                          </select>
                          <small className="form-text text-muted" style={{ color: 'var(--text-muted)' }}>
                            Select the Store ID
                          </small>
                        </div>

                        <div className="form-group">
                          <label htmlFor="posname" style={{ color: 'var(--text)' }}>
                            POS Name <span className="text-danger">*</span>
                          </label>
                          <select
                            className="form-control"
                            id="posname"
                            name="posname"
                            value={formData.posname}
                            onChange={handleInputChange}
                            disabled={loading || !formData.storeid}
                            required
                            style={{
                              backgroundColor: 'var(--surface2)',
                              color: 'var(--text)',
                              border: '1px solid var(--border)'
                            }}
                          >
                            <option value="">Select POS Name</option>
                            {posList.map((pos, index) => (
                              <option key={index} value={pos.value}>
                                {pos.value}
                              </option>
                            ))}
                          </select>
                          <small className="form-text text-muted" style={{ color: 'var(--text-muted)' }}>
                            Select the Point of Sale name
                          </small>
                        </div>

                        <div className="form-group">
                          <label htmlFor="remark" style={{ color: 'var(--text)' }}>
                            Remark
                          </label>
                          <textarea
                            className="form-control"
                            id="remark"
                            name="remark"
                            value={formData.remark}
                            onChange={handleInputChange}
                            rows="3"
                            placeholder="Enter any additional information"
                            style={{
                              backgroundColor: 'var(--surface2)',
                              color: 'var(--text)',
                              border: '1px solid var(--border)',
                              placeholder: { color: 'var(--text-muted)' }
                            }}
                          ></textarea>
                          <small className="form-text text-muted" style={{ color: 'var(--text-muted)' }}>
                            Optional field for additional information
                          </small>
                        </div>

                        <div className="form-group">
                          <div className="form-check">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              id="active"
                              name="active"
                              checked={formData.active}
                              onChange={handleInputChange}
                              style={{
                                backgroundColor: 'var(--surface2)',
                                border: '1px solid var(--border)'
                              }}
                            />
                            <label className="form-check-label" htmlFor="active" style={{ color: 'var(--text)' }}>
                              Active
                            </label>
                          </div>
                          <small className="form-text text-muted" style={{ color: 'var(--text-muted)' }}>
                            Check to mark this record as active
                          </small>
                        </div>
                      </div>

                      <div className="card-footer">
                        <button
                          type="submit"
                          className="btn btn-success"
                          disabled={loading}
                        >
                          {loading ? (
                            <>
                              <span className="spinner-border spinner-border-sm mr-2"></span>
                              Saving...
                            </>
                          ) : (
                            <>
                              <i className="fas fa-save mr-2"></i>
                              Save Record
                            </>
                          )}
                        </button>
                        <button
                          type="button"
                          className="btn btn-secondary ml-2"
                          onClick={() =>
                            navigate(
                              `/subgroupdatalist/${subgroupId}/${groupId}`
                            )
                          }
                          disabled={loading}
                        >
                          <i className="fas fa-times mr-2"></i>Cancel
                        </button>
                      </div>
                    </form>
                  </div>
                </div>

                {/* Info card */}
                <div className="col-lg-6">
                  <div className="card bg-light">
                    <div className="card-header">
                      <h3 className="card-title">
                        <i className="fas fa-info-circle mr-2"></i>Instructions
                      </h3>
                    </div>
                    <div className="card-body" style={{ backgroundColor: 'var(--surface)', color: 'var(--text)' }}>
                      <ul style={{ color: 'var(--text)' }}>
                        <li>
                          <strong style={{ color: 'var(--text)' }}>Merchant ID:</strong> Select the unique
                          merchant identifier from the dropdown
                        </li>
                        <li>
                          <strong style={{ color: 'var(--text)' }}>Store ID:</strong> Select the Store ID
                          (appears after selecting Merchant ID)
                        </li>
                        <li>
                          <strong style={{ color: 'var(--text)' }}>POS Name:</strong> Select the name or identifier
                          for the Point of Sale (appears after selecting Store ID)
                        </li>
                        <li>
                          <strong style={{ color: 'var(--text)' }}>Remark:</strong> Optional field for additional
                          information
                        </li>
                        <li>
                          <strong style={{ color: 'var(--text)' }}>Active:</strong> Check to mark this record as
                          active
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>

      {/* Message display */}
      {message && (
        <div
          style={{
            position: "fixed",
            top: "20px",
            right: "10px",
            width: "25%",
            zIndex: "9999",
          }}
        >
          <div
            className={`alert ${
              messageType === "success" ? "alert-success" : "alert-danger"
            } d-flex align-items-center`}
            role="alert"
            style={{ marginBottom: "10px" }}
          >
            <div>
              {messageType === "success" ? (
                <i className="fas fa-check-circle mr-2"></i>
              ) : (
                <i className="fas fa-exclamation-circle mr-2"></i>
              )}
              {message}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SubGroupDataAdd;

function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(";").shift();
}

